import { useEffect, useRef } from "react";

/**
 * Auto-refresh helper for pages that need to feel "live" without a manual
 * reload. Calls the provided `fn` at a fixed interval and also whenever the
 * browser tab regains visibility or window focus, so a dispatcher who leaves
 * the tab open comes back to fresh data.
 *
 * The callback runs in the background — no loading spinner is toggled — so
 * silent refreshes never disrupt user interaction. If the callback returns a
 * promise it is awaited to avoid overlapping requests.
 *
 * @param {Function} fn      Callback to invoke on each refresh tick.
 * @param {number}   intervalMs  Poll interval in milliseconds. Defaults to 15s.
 * @param {boolean}  enabled  When false, no polling nor listeners are wired.
 */
export default function useAutoRefresh(fn, intervalMs = 15000, enabled = true) {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    if (!enabled) return;
    let busy = false;
    const tick = async () => {
      if (busy || document.hidden) return;
      busy = true;
      try { await fnRef.current?.(); }
      catch (err) { if (process.env.NODE_ENV !== "production") console.debug("useAutoRefresh tick failed:", err?.message || err); }
      finally { busy = false; }
    };
    const onVisible = () => { if (!document.hidden) tick(); };
    const int = setInterval(tick, intervalMs);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearInterval(int);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [intervalMs, enabled]);
}
